// frontend/src/pages/NotificationsPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  MarkChatRead as MarkReadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { notificationsAPI } from '../services/notificationsAPI';
import NotificationCard from '../components/Notifications/NotificationCard';

// ============================================
// СТИЛИЗОВАННЫЕ КОМПОНЕНТЫ
// ============================================

const PageContainer = styled(Container)(({ theme }) => ({
  maxWidth: '800px !important',  // 🔥 ЕЩЕ УЖЕ (было 960px)
  paddingTop: 0,  // 🔥 УБРАЛ ОТСТУП СВЕРХУ
  paddingBottom: theme.spacing(6)
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(2)  // 🔥 НЕБОЛЬШОЙ ОТСТУП СВЕРХУ, НО НЕ БЛОК
}));

// 🔥 ВКЛАДКИ КАК В PROFILEPAGE
const TabsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'transparent',
  borderBottom: 'none',
  position: 'relative',
  marginBottom: theme.spacing(3)
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 48,
  '& .MuiTab-root': {
    minHeight: 48,
    py: 1,
    px: 3,
    borderRadius: 6,
    transition: 'all 0.2s',
    color: 'text.secondary',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.95rem',
    '&:hover': {
      color: 'primary.main',
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    }
  },
  '& .Mui-selected': {
    color: 'primary.main !important',
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    fontWeight: 600
  },
  '& .MuiTabs-indicator': {
    display: 'none'
  }
}));

// 🔥 КНОПКА ОБНОВЛЕНИЯ С БОЛЬШИМ СКРУГЛЕНИЕМ
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 32,  // 🔥 ТАКОЕ ЖЕ СКРУГЛЕНИЕ, КАК У ВКЛАДОК
  border: '0.5px solid',
  borderColor: alpha(theme.palette.primary.main, 0.2),
  padding: theme.spacing(1),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05)
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(8),
  backgroundColor: alpha(theme.palette.primary.main, 0.02),
  borderRadius: theme.spacing(6)  // 🔥 УВЕЛИЧИЛ СКРУГЛЕНИЕ
}));

const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 400
}));

// ============================================
// ОСНОВНОЙ КОМПОНЕНТ
// ============================================

const NotificationsPage = () => {
  const theme = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadNotifications = useCallback(async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setPage(1);
      }

      const params = {
        page: reset ? 1 : page,
        page_size: 20,
        ...(tabValue === 1 && { is_read: false }),
        ...(tabValue === 2 && { is_read: true })
      };

      const data = await notificationsAPI.getAll(params);

      if (reset) {
        setNotifications(data.results || []);
      } else {
        setNotifications(prev => [...prev, ...(data.results || [])]);
      }

      setHasMore(data.next !== null);
      setError(null);
    } catch (err) {
      setError('Не удалось загрузить уведомления');
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [page, tabValue]);

  useEffect(() => {
    loadNotifications(true);
  }, [tabValue]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      setPage(prev => prev + 1);
    }
  };

  useEffect(() => {
    if (page > 1) {
      loadNotifications();
    }
  }, [page]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev =>
        prev.map(n =>
          n.id === id ? { ...n, is_read: true } : n
        )
      );
    } catch (err) {
      console.error('Ошибка при отметке:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, is_read: true }))
      );
    } catch (err) {
      console.error('Ошибка при отметке всех:', err);
    }
  };

  const handleArchive = async (id) => {
    try {
      await notificationsAPI.archive(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Ошибка при архивации:', err);
    }
  };

  const handleNotificationAction = (url) => {
    window.location.href = url;
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <PageContainer>
      {/* 🔥 ЗАГОЛОВОК БЕЗ БЛОКА */}
      <HeaderBox>
        <Typography
          variant="h4"
          fontWeight="700"
          sx={{ color: theme.palette.primary.main }}  // 🔥 КОРПОРАТИВНЫЙ ЗЕЛЕНЫЙ
        >
          Уведомления
          {unreadCount > 0 && (
            <Box
              component="span"
              sx={{
                ml: 1.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 5,
                bgcolor: 'error.main',
                color: 'white',
                fontSize: '0.9rem',
                fontWeight: 600,
                display: 'inline-block'
              }}
            >
              {unreadCount} новых
            </Box>
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {unreadCount > 0 && (
            <Tooltip title="Отметить все как прочитанные">
              <Button
                variant="outlined"
                startIcon={<MarkReadIcon />}
                onClick={handleMarkAllAsRead}
                sx={{ borderRadius: 6 }}  // 🔥 УВЕЛИЧИЛ СКРУГЛЕНИЕ
              >
                Прочитать все
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Обновить">
            <StyledIconButton
              onClick={() => loadNotifications(true)}
              disabled={loading}
            >
              <RefreshIcon />
            </StyledIconButton>
          </Tooltip>
        </Box>
      </HeaderBox>

      {/* ВКЛАДКИ */}
      <TabsContainer>
        <StyledTabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Все" />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Непрочитанные</span>
                {unreadCount > 0 && (
                  <Box
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 3,
                      bgcolor: 'error.main',
                      color: 'white',
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}
                  >
                    {unreadCount}
                  </Box>
                )}
              </Box>
            }
          />
          <Tab label="Прочитанные" />
        </StyledTabs>
      </TabsContainer>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 6 }}>
          {error}
          <Button color="inherit" size="small" onClick={() => loadNotifications(true)} sx={{ ml: 2 }}>
            Повторить
          </Button>
        </Alert>
      )}

      {loading && notifications.length === 0 ? (
        <LoaderContainer>
          <CircularProgress size={60} />
        </LoaderContainer>
      ) : notifications.length > 0 ? (
        <Box>
          {notifications.map(notification => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkRead={handleMarkAsRead}
              onArchive={handleArchive}
              onAction={handleNotificationAction}
            />
          ))}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{ borderRadius: 6 }}
              >
                {loadingMore ? <CircularProgress size={24} /> : 'Загрузить ещё'}
              </Button>
            </Box>
          )}
        </Box>
      ) : (
        <EmptyState>
          <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            У вас пока нет уведомлений
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Здесь будут появляться уведомления о приглашениях, заявках и задачах
          </Typography>
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default NotificationsPage;